import { type ComponentType } from 'react';

import type {
	CommonFilters,
	ConfluenceFilters,
	JiraFilters,
	ThirdPartyFilters,
} from '@atlassian/dynamic-cards-search-client';

import { type ProductKeys } from './products';
import { type FilterKeysRaw } from './schemas/query-params';

export type OptionType<Value = FilterValue<ProductKeys[]>> = {
	[key: string]: any; // Used in OptionType from @atlaskit/select
	isSelected: boolean;
	label: React.ReactNode;
	value: Value;
};

export type FilterPrimitiveType = string | number | boolean;

export type FilterOption<
	Products extends ProductKeys[] = ProductKeys[],
	Value = FilterValue<Products>,
> = OptionType<Value> & {
	avatarUrl?: string;
	filterKey?: string | boolean; // Used to map the initialValue from the queryParams to a filter option
	isDefault?: boolean;
	isSuggested?: boolean;
	products: Products[number][];
	type?: string;
};

export type FilterState<Value, Products extends ProductKeys[]> = {
	readonly boolean: {
		readonly value: boolean;
	};
	readonly select: {
		/* `isHydrated` is the state that we use whether the filter options are up to date.
		 * We need this as we are bootstrapping filter options through a useEffect
		 * which causes filters and its options to be rendered at different times.
		 * It is also needed for non-primitive filters to hydrate the filter options
		 * from differnt data sources.
		 * */
		readonly isHydrated: boolean;

		readonly options: FilterOption<Products, Value>[];
	};
};

export type FilterMultiValue<Products extends ProductKeys[], Value> = Partial<
	Record<Products[number], Value>
>;

export type FilterValue<Products extends ProductKeys[]> =
	| FilterPrimitiveType
	| FilterMultiValue<Products, FilterPrimitiveType>;

export type FilterType<Value, Products extends ProductKeys[]> = keyof FilterState<Value, Products>;

export type Filter<
	ID extends string = string,
	Products extends ProductKeys[] = ProductKeys[],
	Value = FilterValue<Products>,
	Type extends FilterType<Value, Products> = FilterType<Value, Products>,
> = {
	// Set by the adding dropdown, to allow a filter to show without a selected value, should clear on blur
	readonly hasBeenAdded?: boolean;
	// Set by a filter when it has a value, used to show/hide with the dropdown
	readonly hasSelectedValue?: boolean;
	readonly id: ID;
	readonly initialValue?: FilterKeysRaw;
	readonly products: Products;
	readonly state: FilterState<Value, Products>[Type];
	readonly type: Type;
};

export type Filters<Products extends ProductKeys[] = ProductKeys[]> = Record<
	string,
	Filter<string, Products, FilterValue<Products>, FilterType<FilterValue<Products>, Products>>
>;

export type FixedSizeArray<N extends number> = N extends 0
	? never[]
	: {
			length: N;
		};

export type FilterProduct<F extends Filter> =
	F['products'] extends FixedSizeArray<1> ? F['products'][0] : F['products'][number] | undefined;

export type FilterComponentProps<F extends Filter> = {
	bootstrapState: (
		state: F['state'],
		isSelected?: boolean,
		hasBeenAdded?: boolean,
	) => void | Promise<void>;
	filterIsVisible?: boolean;
	hasBeenAdded: F['hasBeenAdded'];
	initialValue: F['initialValue'];
	product: FilterProduct<F>;
	setState: (state: F['state'], isSelected?: boolean) => void | Promise<void>;
	state: F['state'];
};

export type FilterComponent<F extends Filter> = ComponentType<FilterComponentProps<F>>;
export interface UserDetails {
	email?: string | null;
	id: string | null;
	name: string | null;
}

export enum UserStatus {
	Unauthenticated = 'unauthenticated',
	Anonymous = 'anonymous',
	Authenticated = 'authenticated',
}

export interface UserDetailsWithStatus extends UserDetailsWithPicture {
	status: UserStatus;
}

export interface UserDetailsWithPicture extends UserDetails {
	picture?: string | null;
}

export interface SearchResultAncestor {
	href: string;
	id: string;
	title: string;
}

export type SelectedFiltersType = {
	commonFilters: CommonFilters;
	confluenceFilters: ConfluenceFilters;
	jiraFilters: JiraFilters;
	thirdPartyFilters: ThirdPartyFilters;
};

type onInputDropdownResultSelectArgs = {
	title: string;
	url?: string;
};

export type onInputDropdownResultSelect = (args: onInputDropdownResultSelectArgs) => void;

type onInputSubmitArgs = {
	query: string;
};

export type onInputSubmit = (args: onInputSubmitArgs) => void;

export type ExperimentConfig = {
	backendExperiment: any;
	frontendExperiments: any;
};
